import qs from 'qs';

import axios from '../../../shared/axios/axios';
import { normalizeResource } from '../../../shared/utils/files';

import * as sharedActions from '../../../shared/store/actions';

// ncFileManager variable is defined in Web Forms
function getFileManagerRequestParams() {
  return {
    insuredId: window['ncFileManager']['insuredId'],
    policyId: window['ncFileManager']['policyId'],
    noteId: window['ncFileManager']['noteId'],
    taskId: window['ncFileManager']['taskId'],
    agencyId: window['ncFileManager']['agencyId'],
    agentId: window['ncFileManager']['agentId'],
    opportunityId: window['ncFileManager']['opportunityId'],
    claimId: window['ncFileManager']['claimId'],
    serviceRequestId: window['ncFileManager']['serviceRequestId'],
    ownerType: window['ncFileManager']['ownerType'],
    currentFullURL: window.location.href,
  };
}

/**
 * hasSignedIn
 *
 * @returns {boolean}
 */
function hasSignedIn() {
  return true;
}

/**
 * Init API
 *
 * @returns {Promise<{apiInitialized: boolean, apiSignedIn: boolean}>}
 */
function init() {
  return {
    apiInitialized: true,
    apiSignedIn: true,
  };
}

async function getCapabilitiesForResource(options, resource) {
  return resource.capabilities || [];
}

async function saveFilesControlConfiguration(controlConfig, isSendFiles) {
  const route = `/files/save-files-control-configuration`;

  const response = await axios.post(route, { config: controlConfig, isSendFiles });

  return response.data;
}

async function copyDefaultFolderStructure() {
  const route = `/files/copy-default-folder-structure`;
  const params = {
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return response.data;
}

async function getResourceById(options, id) {
  const route = `/files/byid/`;
  const params = {
    id,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return normalizeResource(response.data);
}

async function getChildrenForId(options, { id, systemFolderType, sortBy = 'name', sortDirection = 'ASC' }) {
  const route = id.toString().endsWith('_onedrive')
    ? `/onedrive/children`
    : id.toString().endsWith('_googledrive')
    ? `/googledrive/children`
    : `/files/children`;

  const params = {
    id,
    systemFolderType,
    orderBy: sortBy,
    orderDirection: sortDirection,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return response.data.items.map(normalizeResource);
}

async function getParentsForId(options, id) {
  const route = `/files/ancestors-byid/`;
  const params = {
    id,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);
  return response.data;
}

async function getFolderChildrenForId(id) {
  const route = `/files/folder-children-byid/`;
  const params = {
    id,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);
  return response.data;
}

async function getBaseResource(options) {
  const route = `/files/root`;
  const params = {
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return normalizeResource(response.data);
}

async function getIdForPartPath(options, currId, systemFolderType, pathArr) {
  const resourceChildren = await getChildrenForId(options, { id: currId, systemFolderType });
  for (let i = 0; i < resourceChildren.length; i++) {
    const resource = resourceChildren[i];
    if (resource.name === pathArr[0]) {
      if (pathArr.length === 1) {
        return resource.id;
      } else {
        return getIdForPartPath(options, resource.id, resource.systemFolderType, pathArr.slice(1));
      }
    }
  }

  return null;
}

async function getIdForPath(options, path) {
  const resource = await getBaseResource(options);
  const pathArr = path.split('/');

  if (pathArr.length === 0 || pathArr.length === 1 || pathArr[0] !== '') {
    return null;
  }

  if (pathArr.length === 2 && pathArr[1] === '') {
    return resource.id;
  }

  return getIdForPartPath(options, resource.id, resource.systemFolderType, pathArr.slice(1));
}

async function getParentIdForResource(options, resource) {
  return resource.parentId;
}

async function getPresignedUrlsForUpload(apiOptions, formData) {
  const route = `/files/get-presigned-urls-for-upload`;

  return axios.post(route, formData);
}

function uploadFileToS3Id({ uploadUrl, file, onProgress }) {
  const formData = new FormData();

  formData.append(file.name, file.file);

  return new Promise((resolve, reject) => {
    axios
      .put(uploadUrl, file.file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: (event) => {
          const percent = Math.round(event.loaded / (event.total / 100));

          onProgress(file.notificationChildId, percent);
        },
        timeout: 300000, // 5 minutes. should be enough to upload big enough file
      })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
}

function uploadFileToOneDrive({ file, parentId, onProgress }) {
  const route = `/onedrive/upload`;
  const formData = new FormData();

  formData.append(file.name, file.file);
  formData.append('parentId', parentId);

  return new Promise((resolve, reject) => {
    axios
      .post(route, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },

        onUploadProgress: (event) => {
          const percent = Math.round(event.loaded / (event.total / 100));

          onProgress(file.notificationChildId, percent);
        },
      })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
}

function uploadFileToGoogleDrive({ file, parentId, onProgress }) {
  const route = `/googledrive/upload`;
  const formData = new FormData();

  formData.append(file.name, file.file);
  formData.append('parentId', parentId);

  return new Promise((resolve, reject) => {
    axios
      .post(route, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },

        onUploadProgress: (event) => {
          const percent = Math.round(event.loaded / (event.total / 100));

          onProgress(file.notificationChildId, percent);
        },
      })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
}

async function confirmUpload({ apiOptions, parentId, fileId, fileName, fileLength }) {
  const route = `/files/confirm-upload`;
  const params = {
    parentId: parentId,
    fileId: fileId,
    name: fileName,
    fileLength: fileLength,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return normalizeResource(response.data);
}

async function downloadResources({ apiOptions, resources, onProgress }) {
  const downloadUrl = `/files/download`;

  const params = {
    items: resources.map((r) => r.id),
    ...getFileManagerRequestParams(),
  };

  const res = await axios.post(downloadUrl, params, {
    responseType: 'blob',
    onDownloadProgress: (event) => {
      onProgress(undefined, event.percent);
    },
  });

  let fileName = '';
  const disposition = res.request.getResponseHeader('Content-Disposition');
  if (disposition && disposition.indexOf('attachment') !== -1) {
    const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = fileNameRegex.exec(disposition);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }
  }

  return { body: res.data, fileName };
}

async function createFolder(options, parentId, folderName) {
  const route = parentId.toString().endsWith('_onedrive')
    ? `/onedrive/create-folder`
    : parentId.toString().endsWith('_googledrive')
    ? `/googledrive/create-folder`
    : `/files/create-folder`;
  const params = {
    parentId,
    name: folderName,
    type: 'dir',
    ...getFileManagerRequestParams(),
  };
  return axios.post(route, params);
}

async function createFolderTree(parentId, folderName) {
  const route = `/files/create-folder-tree`;
  var sss = getFileManagerRequestParams();
  const params = {
    parentId,
    folders: folderName,
    requestData: sss,
  };

  return axios.post(route, params);
}

function getResourceName(apiOptions, resource) {
  return resource.name;
}

function getResourceUrl(options, id) {
  return axios.get(
    id.toString().endsWith('_onedrive')
      ? `/onedrive/direct-url/${id}`
      : id.toString().endsWith('_googledrive')
      ? `/googledrive/direct-url/${id}`
      : `/files/direct-url/${id}`,
  );
}

async function renameResource(options, id, newName, parentId) {
  const route = `/files/rename`;
  const params = {
    id,
    name: newName,
    parentId,
    ...getFileManagerRequestParams(),
  };
  return axios.post(route, params);
}

async function updateFile(options, fileInfo) {
  const route = `/files/update`;
  const params = {
    ...fileInfo,
    ...getFileManagerRequestParams(),
  };
  return axios.put(route, params);
}

async function removeResources(options, resources) {
  const route = `/files/remove`;
  const params = {
    ids: resources.map((r) => r.id),
    ...getFileManagerRequestParams(),
  };
  return axios.post(route, params);
}

async function getFilesInDestinationFolder(options, sourceFileNames, targetItemId, excludeFileIds) {
  return new Promise((resolve, reject) => {
    const route = `/files/by-parent-and-names`;
    const params = {
      fileNames: sourceFileNames,
      parentId: targetItemId,
      excludeFileIds: excludeFileIds ?? [],
    };

    axios
      .post(route, params)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function moveResources(options, sourceItems, targetItemId) {
  const route = `/files/move`;
  const params = {
    sourceItems: sourceItems,
    targetId: targetItemId,
    ...getFileManagerRequestParams(),
  };
  return axios.post(route, params);
}

async function copyResources(options, sourceItems, targetItemId) {
  const route = `/files/copy`;
  const params = {
    sourceItems: sourceItems,
    targetId: targetItemId,
    ...getFileManagerRequestParams(),
  };
  return axios.put(route, params);
}

async function search(options, formData) {
  const route = `/files/search`;

  const params = {
    ...formData,
    ...getFileManagerRequestParams(),
  };

  const response = await axios.post(route, params);

  return response.data.items.map((response) => normalizeResource({ ...response, searchString: formData.searchValue }));
}

async function getSendFileInfo(options) {
  return new Promise((resolve, reject) => {
    const route = `/files/get-send-file-info`;

    axios
      .get(route, {
        params: {
          insuredId: window['ncFileManager']['insuredId'],
          receiptId: window['ncFileManager']['receiptId'],
          isReceipt: window['ncFileManager']['isReceipt'],
        },
        paramsSerializer: (params) => {
          return qs.stringify(params);
        },
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function sendFile(sendFileInfo) {
  return new Promise((resolve, reject) => {
    const route = `/files/send`;

    axios
      .post(route, sendFileInfo)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function replaceMessagePlaceholders(message) {
  return sharedActions.replaceMessagePlaceholders(
    message,
    window['ncFileManager']['insuredId'],
    window['ncFileManager']['policyId'],
    window['ncFileManager']['receiptId'],
  )(() => {} /* fake dispatch method */);
}

//#region OneDrive integration

async function getOneDriveCodeRequestUrl() {
  const route = `/onedrive/code-request-url`;

  const response = await axios.get(route);

  return response.data;
}

async function getOneDriveLogout() {
  const route = '/onedrive/logout-request';

  const response = await axios.get(route);

  return response.data;
}

async function getOneDriveFolderById(id, oneDriveLoginCallback) {
  return new Promise((resolve, reject) => {
    let idRouteParam = '';

    if (id) {
      idRouteParam = `?id=${id}`;
    }

    const route = `/onedrive/folder-by-id${idRouteParam}`;

    axios
      .get(route)
      .then((response) => {
        resolve(normalizeResource(response.data));
      })
      .catch((error) => {
        reject(error);

        if (
          typeof oneDriveLoginCallback === 'function' &&
          error.response &&
          error.response.data &&
          error.response.data.errorCode === 'OneDriveUnauthenticated'
        ) {
          oneDriveLoginCallback();
        }
      });
  });
}

async function getOneDriveFolderChildren(params, oneDriveLoginCallback) {
  return new Promise((resolve, reject) => {
    const route = `/onedrive/children`;

    axios
      .post(route, params)
      .then((response) => {
        resolve(response.data.items.map(normalizeResource));
      })
      .catch((error) => {
        reject(error);

        if (
          typeof oneDriveLoginCallback === 'function' &&
          error.response &&
          error.response.data &&
          error.response.data.errorCode === 'OneDriveUnauthenticated'
        ) {
          oneDriveLoginCallback();
        }
      });
  });
}

async function assignRootFolderExternalId(externalId) {
  return new Promise((resolve, reject) => {
    const route = `/onedrive/assign-root-folder-externalId`;

    axios
      .post(route, {
        truckingCompanyId: window['ncFileManager']['insuredId'],
        externalId,
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function removeRootFolderExternalId() {
  return new Promise((resolve, reject) => {
    const route = `/onedrive/remove-root-folder-externalId`;

    axios
      .post(route, { truckingCompanyId: window['ncFileManager']['insuredId'] })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function getRootFolderExternalId() {
  return new Promise((resolve, reject) => {
    const route = `/onedrive/root-folder-externalId`;

    axios
      .get(route, {
        params: { truckingCompanyId: window['ncFileManager']['insuredId'] },
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

//#endregion OneDrive integration

//#region GoogleDrive integration

async function getGoogleDriveCodeRequestUrl() {
  const route = `/googledrive/code-request-url`;

  const response = await axios.get(route);

  return response.data;
}

async function getGoogleDriveFolderById(id, googleDriveLoginCallback) {
  return new Promise((resolve, reject) => {
    let idRouteParam = '';

    if (id) {
      idRouteParam = `?id=${id}`;
    }

    const route = `/googledrive/folder-by-id${idRouteParam}`;

    axios
      .get(route)
      .then((response) => {
        resolve(normalizeResource(response.data));
      })
      .catch((error) => {
        reject(error);

        if (
          typeof googleDriveLoginCallback === 'function' &&
          error.response &&
          error.response.data &&
          error.response.data.errorCode === 'GoogleDriveUnauthenticated'
        ) {
          googleDriveLoginCallback();
        }
      });
  });
}

async function getGoogleDriveFolderChildren(params, googleDriveLoginCallback) {
  return new Promise((resolve, reject) => {
    const route = `/googledrive/children`;

    axios
      .post(route, params)
      .then((response) => {
        resolve(response.data.items.map(normalizeResource));
      })
      .catch((error) => {
        reject(error);

        if (
          typeof googleDriveLoginCallback === 'function' &&
          error.response &&
          error.response.data &&
          error.response.data.errorCode === 'GoogleDriveUnauthenticated'
        ) {
          googleDriveLoginCallback();
        }
      });
  });
}

async function assignGoogleDriveRootFolderExternalId(externalId) {
  return new Promise((resolve, reject) => {
    const route = `/googledrive/assign-root-folder-externalId`;

    axios
      .post(route, {
        truckingCompanyId: window['ncFileManager']['insuredId'],
        externalId,
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function removeGoogleDriveRootFolderExternalId() {
  return new Promise((resolve, reject) => {
    const route = `/googledrive/remove-root-folder-externalId`;

    axios
      .post(route, { truckingCompanyId: window['ncFileManager']['insuredId'] })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

async function getGoogleDriveRootFolderExternalId() {
  return new Promise((resolve, reject) => {
    const route = `/googledrive/root-folder-externalId`;

    axios
      .get(route, {
        params: { truckingCompanyId: window['ncFileManager']['insuredId'] },
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

//#endregion GoogleDrive integration

//#region RabbitSign

async function getPdfFiles(params) {
  return new Promise((resolve, reject) => {
    const route = `/form-editor/get-pdf-files`;

    axios
      .post(route, params)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

//#endregion RabbitSign

export default {
  init,
  hasSignedIn,
  saveFilesControlConfiguration,
  copyDefaultFolderStructure,
  getIdForPath,
  getResourceById,
  getCapabilitiesForResource,
  getChildrenForId,
  getResourceUrl,
  getParentsForId,
  getFolderChildrenForId,
  getParentIdForResource,
  getResourceName,
  createFolder,
  createFolderTree,
  downloadResources,
  renameResource,
  removeResources,
  uploadFileToS3Id,
  uploadFileToOneDrive,
  uploadFileToGoogleDrive,
  confirmUpload,
  getPresignedUrlsForUpload,
  getFilesInDestinationFolder,
  moveResources,
  updateFile,
  copyResources,
  search,
  getSendFileInfo,
  sendFile,
  replaceMessagePlaceholders,
  getOneDriveCodeRequestUrl,
  getOneDriveLogout,
  getOneDriveFolderById,
  getOneDriveFolderChildren,
  assignRootFolderExternalId,
  removeRootFolderExternalId,
  getRootFolderExternalId,
  getGoogleDriveCodeRequestUrl,
  getGoogleDriveFolderById,
  getGoogleDriveFolderChildren,
  assignGoogleDriveRootFolderExternalId,
  removeGoogleDriveRootFolderExternalId,
  getGoogleDriveRootFolderExternalId,
  getPdfFiles,
};
